import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Input } from '@/components/ui/input'
import { useSkuSearch } from '@/hooks/useSkuSearch'
import type { SkuSearchResult } from '@/types/sku'
import { NewSkuBadge } from './NewSkuBadge'

export interface SkuSaveData {
  skuNumber: string
  description: string | null
  isNewSku: boolean
  // Additional fields from SKU search for auto-fill
  vendorId: string | null
  vendorName: string | null
  department: string | null
  class_: string | null
  targetRetail: string | null
  targetCost: string | null
}

interface SkuSearchCellProps {
  value: string | null
  isNewSku?: boolean
  onSave: (data: SkuSaveData) => void
  isEditing: boolean
  onCancelEdit: () => void
  allowNewSku?: boolean
  onTab?: (shiftKey: boolean) => void
}

export function SkuSearchCell({
  value,
  isNewSku,
  onSave,
  isEditing,
  onCancelEdit,
  allowNewSku = true,
  onTab,
}: SkuSearchCellProps) {
  const [searchQuery, setSearchQuery] = useState(value || '')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [showDropdown, setShowDropdown] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  })
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data, isLoading } = useSkuSearch({
    query: searchQuery,
    enabled: isEditing && searchQuery.length >= 1,
  })

  const results = data?.data ?? []

  // Check if there's an exact match in results
  const hasExactMatch = results.some(
    result => result.sku_number.toLowerCase() === searchQuery.toLowerCase()
  )

  // Determine if we should show the "New SKU" option
  const showNewSkuOption =
    allowNewSku && searchQuery.length >= 1 && !hasExactMatch

  // Total items including the "New SKU" option
  const totalItems = results.length + (showNewSkuOption ? 1 : 0)

  // Reset state when editing starts/stops
  useEffect(() => {
    if (isEditing) {
      setSearchQuery(value || '')
      setHighlightedIndex(-1)
      setShowDropdown(true)
    } else {
      setShowDropdown(false)
    }
  }, [isEditing, value])

  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Show dropdown when we have results or can add new
  useEffect(() => {
    if (
      isEditing &&
      (results.length > 0 ||
        showNewSkuOption ||
        (searchQuery.length === 0 && value))
    ) {
      setShowDropdown(true)
    }
  }, [isEditing, results.length, showNewSkuOption, searchQuery, value])

  // Calculate dropdown position and update on scroll
  useEffect(() => {
    if (!showDropdown || !containerRef.current) return

    const updatePosition = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()

      // Check if we should show dropdown above or below
      const spaceBelow = window.innerHeight - rect.bottom
      const dropdownHeight =
        dropdownRef.current?.getBoundingClientRect().height || 240
      const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight

      setDropdownPosition({
        top: showAbove ? rect.top - dropdownHeight : rect.bottom,
        left: rect.left,
        width: Math.max(rect.width, 300),
      })
    }

    // Use rAF to ensure DOM is settled before initial measurement
    const rafId = requestAnimationFrame(updatePosition)

    // Update position on scroll (any scrollable parent)
    const handleScroll = () => updatePosition()
    const handleResize = () => updatePosition()

    // Listen to scroll events on window and any parent with overflow
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    }
  }, [showDropdown])

  const handleSelectSku = (result: SkuSearchResult) => {
    onSave({
      skuNumber: result.sku_number,
      description: result.description,
      isNewSku: false,
      vendorId: result.vendor_id,
      vendorName: result.vendor_name,
      department: result.department_code,
      class_: result.category_code,
      targetRetail: result.last_retail,
      targetCost: result.last_cost,
    })
    setShowDropdown(false)
  }

  const handleSelectNewSku = () => {
    onSave({
      skuNumber: searchQuery,
      description: null,
      isNewSku: true,
      vendorId: null,
      vendorName: null,
      department: null,
      class_: null,
      targetRetail: null,
      targetCost: null,
    })
    setShowDropdown(false)
  }

  const handleClear = () => {
    onSave({
      skuNumber: '',
      description: null,
      isNewSku: false,
      vendorId: null,
      vendorName: null,
      department: null,
      class_: null,
      targetRetail: null,
      targetCost: null,
    })
    setShowDropdown(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()

    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
      e.preventDefault()

      // Copy the currently highlighted dropdown option instead of the cell value.
      if (highlightedIndex >= 0) {
        if (highlightedIndex < results.length) {
          const highlighted = results[highlightedIndex]
          navigator.clipboard.writeText(highlighted.sku_number).catch(() => {})
        } else if (showNewSkuOption && highlightedIndex === results.length) {
          navigator.clipboard.writeText(searchQuery).catch(() => {})
        }
      }

      return
    }

    if (!showDropdown || totalItems === 0) {
      if (e.key === 'Escape') {
        e.preventDefault()
        setSearchQuery(value || '')
        onCancelEdit()
      } else if (e.key === 'Enter' && searchQuery.length === 0 && value) {
        e.preventDefault()
        handleClear()
      } else if (e.key === 'Tab') {
        e.preventDefault()
        onCancelEdit()
        onTab?.(e.shiftKey)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => (prev + 1) % totalItems)
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => (prev - 1 + totalItems) % totalItems)
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0) {
          // Check if the highlighted item is a search result or the "New SKU" option
          if (highlightedIndex < results.length) {
            handleSelectSku(results[highlightedIndex])
          } else if (showNewSkuOption) {
            handleSelectNewSku()
          }
        } else if (allowNewSku && searchQuery.length > 0) {
          // If nothing is highlighted but there's input, treat as new SKU
          handleSelectNewSku()
        }
        break
      case 'Tab':
        e.preventDefault()
        if (highlightedIndex >= 0) {
          if (highlightedIndex < results.length) {
            handleSelectSku(results[highlightedIndex])
          } else if (showNewSkuOption) {
            handleSelectNewSku()
          }
        } else {
          onCancelEdit()
        }
        onTab?.(e.shiftKey)
        break
      case 'Escape':
        e.preventDefault()
        setSearchQuery(value || '')
        onCancelEdit()
        break
    }
  }

  const handleBlur = (e: React.FocusEvent) => {
    // Check if the related target is within the dropdown
    if (dropdownRef.current?.contains(e.relatedTarget as Node)) {
      return
    }
    // Delay to allow click on dropdown items
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setShowDropdown(false)
        onCancelEdit()
      }
    }, 150)
  }

  if (isEditing) {
    return (
      <div className="relative" ref={containerRef}>
        <Input
          ref={inputRef}
          value={searchQuery}
          onChange={e => {
            setSearchQuery(e.target.value)
            setHighlightedIndex(-1)
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className="h-8 border-0 border-b-2 border-blue-500 rounded-none px-1 focus-visible:ring-0 focus-visible:ring-offset-0"
          placeholder="Search SKU..."
        />

        {showDropdown &&
          (searchQuery.length >= 1 || value) &&
          createPortal(
            <div
              ref={dropdownRef}
              data-sku-dropdown
              className="dag-dropdown-portal fixed z-[9999] pointer-events-auto max-h-60 overflow-auto rounded border border-[#D6DAE1] bg-white shadow-md"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                minWidth: dropdownPosition.width,
              }}
            >
              {searchQuery.length === 0 && value && (
                <div
                  className="cursor-pointer px-2 py-1.5 text-sm hover:bg-muted flex items-center gap-2 text-muted-foreground"
                  onMouseDown={e => e.preventDefault()}
                  onClick={handleClear}
                >
                  Clear value
                </div>
              )}
              {searchQuery.length >= 1 && (
                <>
                  {isLoading ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      Searching...
                    </div>
                  ) : (
                    <>
                      {results.map((result, index) => (
                        <div
                          key={result.sku_id ?? result.sku_number}
                          className={`cursor-pointer px-2 py-1.5 text-sm hover:bg-muted flex items-center justify-between gap-2 ${
                            highlightedIndex === index ? 'bg-muted' : ''
                          }`}
                          onMouseEnter={() => setHighlightedIndex(index)}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => handleSelectSku(result)}
                        >
                          <span className="font-medium text-[#556374]">
                            {result.sku_number}
                          </span>
                          {result.description && (
                            <span className="text-xs text-[#556374] truncate max-w-[180px]">
                              {result.description}
                            </span>
                          )}
                        </div>
                      ))}

                      {showNewSkuOption && (
                        <div
                          className={`cursor-pointer px-2 py-1.5 text-sm flex items-center justify-between gap-2 ${
                            highlightedIndex === results.length
                              ? 'bg-accent'
                              : ''
                          }`}
                          onMouseEnter={() =>
                            setHighlightedIndex(results.length)
                          }
                          onMouseDown={e => e.preventDefault()}
                          onClick={handleSelectNewSku}
                        >
                          <span className="font-medium text-primary">
                            {searchQuery}
                          </span>
                          <span className="text-xs italic text-primary">
                            New SKU
                          </span>
                        </div>
                      )}

                      {results.length === 0 && !showNewSkuOption && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          No results found
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>,
            document.body
          )}
      </div>
    )
  }

  return (
    <div className="min-h-[2rem] flex items-center gap-2 px-1">
      <span>{value || '-'}</span>
      {allowNewSku && isNewSku && <NewSkuBadge />}
    </div>
  )
}
